import React, { useState, useEffect, useImperativeHandle, useRef, useCallback, useMemo } from "react";
import RadioButtonGroup from "../../elements/radio-button-group";
import { generateReactKey } from "../../utils/reactKey";
import Icon from "../../elements/icon";
import Heading from "../../elements/heading";
import api from "../../utils/api";
import { connect, useSelector } from "react-redux";
import { selectUserState, staffInfoSelector } from "../../selectors/application";
import { pdpBillingSelector, pdpShippingSelector } from "../../selectors/pdp";
//import { shippingInfoSelector } from "../../selectors/gears-checkout";
import { S_MARKET, webStorage } from "../../utils/webStorage";
import "./style.scss";
import AccountRemoveAddressModal from "../account-remove-address-modal";
import PdpShippingInfoForm from "../pdp-shipping-info-form";
import { getMarketCountry, isLoggedIn } from "../../services/siteService";
import AddressInformation from "../address-information";
import { SuperButton } from "../../elements";
import { isEmptyObject } from "../../utils/siteUtils";

const PdpShippingInfo = React.forwardRef(({ fields, onPostalCodeChange, onCityChange, addressList, addressLoaded, noAddress, loadAddressList, addAddress, updateAddress, removeAddress, visible }, ref) => {
  /*Redux start*/
  const user = useSelector(selectUserState);
  const staffInfo = useSelector(staffInfoSelector);
  const billingInfo = useSelector(pdpBillingSelector);
  const shippingInfo = useSelector(pdpShippingSelector);
  /*Redux end*/
  const { addressUnsavedMessage, addressMissingMessage, removeAddressModal, ctaNameChange, ctaTypeChange, colorSchemeChange, iconNameChange } = fields;
  /**
   * @type {React.MutableRefObject<HTMLElement|NULL>}
   */
  const componentRoot = useRef();
  /**
   * @type {React.MutableRefObject<PdpEditAddressFormRef|NULL>}
   */
  const addAddressRef = useRef();
  /**
   * @type {React.MutableRefObject<PdpEditAddressFormRef|NULL>}
   */
  const editAddressRef = useRef(null);
  const [marketCountry, setMarketCountry] = useState(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [showRemoveAddressModal, setShowRemoveAddressModal] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [addressEditing, setAddressEditing] = useState(false);
  const [formError, setFormError] = useState(null);

  useImperativeHandle(ref, () => ({
    validateForm() {
      if (isLoggedIn() && !noAddress) {
        if (editAddress) {
          editAddressRef.current.setError(addressUnsavedMessage.value);
          editAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        } else if (selectedAddress) {
          if (selectedAddress.address) {
            return {
              isValid: true,
              formData: selectedAddress.address
            };
          } else {
            addAddressRef.current.setError(addressUnsavedMessage.value);
            addAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
            return {
              isValid: false,
              formData: null
            };
          }
        } else {
          setFormError(addressMissingMessage.value);
          componentRoot.current.scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        }
      } else {
        return addAddressRef.current.validateForm();
      }
    }
  }));
  useEffect(() => {
    if (addAddressRef.current) {
      setDefaultAddress({
        street: shippingInfo?.street,
        apartment: shippingInfo?.apartment,
        postalCode: shippingInfo?.postalCode,
        city: shippingInfo?.city
      });
    }
  }, [user, addAddressRef, shippingInfo]);

  useEffect(() => {
    if (!noAddress) {
      let _selectedAddress;
      if (selectedAddress && selectedAddress.address) {
        _selectedAddress = addressList.find(x => x.value === selectedAddress.address?.addressId);
      }
      if (!_selectedAddress) {
        _selectedAddress = addressList.find(x => x.isNew);
      }
      if (!_selectedAddress && shippingInfo?.addressId) {
        _selectedAddress = addressList.find(x => x.value === shippingInfo.addressId);
      }
      if (!_selectedAddress) {
        _selectedAddress = addressList.find(x => x.address?.defaultFlag == "1");
      }
      if (!_selectedAddress) {
        _selectedAddress = addressList[0];
      }
      setSelectedAddress(_selectedAddress);
      onPostalCodeChange && onPostalCodeChange(_selectedAddress.address.postalCode);
      onCityChange && onCityChange(_selectedAddress.address.city);
    }
  }, [addressList, noAddress, shippingInfo]);
  const onAddressChange = address => {
    if (selectedAddress !== address) {
      setSelectedAddress(address);
      onPostalCodeChange && onPostalCodeChange(address.address.postalCode);
      onCityChange && onCityChange(address.address.city);
      if (editAddress && editAddress !== address) {
        setEditAddress(null);
      }
      if (address.value !== "new") {
        setAddressEditing(false);
      } else {
        setDefaultAddress({
          street: null,
          apartment: null,
          postalCode: null,
          city: null
        });
      }
    }
  };
  const formatSavedAddress = address => {
    let street = `${address?.street || ""} ${address?.apartment || ""}`;
    let zipCity = `${address?.postalCode || ""} ${address?.city || ""}`;
    return [street, zipCity];
  };
  const removeAddressModalProps = {
    ...removeAddressModal.fields,
    showModal: showRemoveAddressModal,
    onModalClose: evt => {
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    },
    onSubmit: async evt => {
      await api.address.delete(null, {
        addressId: addressToRemove.value
      });
      removeAddress(addressToRemove);
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    }
  };
  const onEditClick = (evt, option) => {
    evt.stopPropagation();
    setEditAddress(option);
    setSelectedAddress(option);
  };
  const onRemoveClick = (evt, option) => {
    evt.stopPropagation();
    setAddressToRemove(option);
    setShowRemoveAddressModal(true);
  };
  const addAddressFormProps = {
    fields,
    ref: addAddressRef,
    address: defaultAddress,
    savable: !noAddress,
    onPostalCodeChange(evt, postalCode) {
      onPostalCodeChange && onPostalCodeChange(postalCode);
    },
    onCityChange(evt, city) {
      onCityChange && onCityChange(city);
    },
    onSaveSuccess: async newAddress => {
      setEditAddress(null);
      const addedAddress = await addAddress(newAddress);
      setSelectedAddress(addedAddress);
      setAddressEditing(false);
    }
  };
  const ctaChangeAddressProps = ctaNameChange.value && {
    buttonText: ctaNameChange.value,
    ctaType: ctaTypeChange,
    colorScheme: colorSchemeChange,
    iconName: iconNameChange,
    callback(evt) {
      setAddressEditing(true);
    }
  };
  const shippingLayout = option => {
    if (option.address) {
      if (editAddress === option) {
        const editAddressFormProps = {
          fields,
          ref: editAddressRef,
          address: option.address,
          async onSaveSuccess() {
            const updatedAddress = await updateAddress(editAddress);
            setSelectedAddress(updatedAddress);
            setEditAddress(null);
            setAddressEditing(false);
          }
        };
        return (
          <div className="edit-address-block" key={generateReactKey()} onClick={e => e.stopPropagation()}>
            <p className="label">Edit Address</p>
            <PdpShippingInfoForm {...editAddressFormProps} />
          </div>
        );
      } else {
        return (
          <div className="saved-address-block" key={generateReactKey()}>
            <div className="body-container">
              {formatSavedAddress(option.address).map(row => {
                return (
                  <p className="content-row" key={generateReactKey()}>
                    {row}
                  </p>
                );
              })}
            </div>
            <div className="icons">
              {option.address.addressId !== billingInfo?.addressId && <Icon name="Edit" colorScheme="outline-green" className="icon --visible" onClick={evt => onEditClick(evt, option)} />}
              {selectedAddress !== option && !noAddress && option.address.defaultFlag != "1" && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveClick(evt, option)} />}
            </div>
          </div>
        );
      }
    } else {
      return <p className="label">{option.label}</p>;
    }
  };
  if (visible && addressLoaded) {
    return (
      <div className="shipping-info" ref={componentRoot}>
        {/*<div className="title">*/}
        {/*  <Heading type={headingType} html={heading} />*/}
        {/*</div>*/}
        {!addressEditing && selectedAddress && <AddressInformation className="default-address" address={selectedAddress.address} showCountry={false} />}

        {isLoggedIn() && addressEditing && !noAddress && <RadioButtonGroup options={addressList} html={shippingLayout} alignment="top" onChange={onAddressChange} selectedOption={selectedAddress} />}

        {(!isLoggedIn() || (selectedAddress && selectedAddress.value === "new") || noAddress) && <PdpShippingInfoForm {...addAddressFormProps} />}

        {formError && <div className="failed-message">{formError}</div>}

        {!addressEditing && !noAddress && (
          <div className="cta-edit-address">
            <SuperButton {...ctaChangeAddressProps} />
          </div>
        )}
        <AccountRemoveAddressModal {...removeAddressModalProps} />
      </div>
    );
  } else {
    return null;
  }
});
PdpShippingInfo.displayName = "PdpShippingInfo";
export default PdpShippingInfo;
